import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  SxProps,
  Typography,
  Zoom,
  useTheme,
} from '@mui/material';
import { FC, RefObject, createRef, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAuth } from '@/logic/state';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { PROFILE_CARDS_TRANSITION_TIMEOUT } from '@/constants/profile';
import { CashbackAccordionDetails } from './cashback-accordion-details';

import { CashbackInfo } from './cashback-info';
import { WalletNotVerifiedInfo } from './wallet-not-verified-info';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useCashbackBalance } from '../../hooks/use-cashback-balance';
import { useRouter } from 'next/router';
import { useScrollToItem } from '@/logic/hooks';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import { CashbackNotCompletedProfileInfo } from './cashback-not-completed-profile-info';
import { useGetTransactions } from '@/features/wallet/services/_api';
import { CASHBACK } from '../../services/types';
import { useUserBalance } from '@/features/wallet/hooks/use-user-balance';

const CashbackIcon = '/icons/profile/transactions/cashback.svg';
const NotfoundIcon = '/icons/shared/not-found-no-data.svg';

interface CashbackCollapseProps {
  accordionSx: SxProps;
  accordionBoxIconSx: SxProps;
  accordionTitleSx: SxProps;
}
const CashbackCollapse: FC<CashbackCollapseProps> = ({
  accordionSx,
  accordionBoxIconSx,
  accordionTitleSx,
}) => {
  const theme = useTheme();
  const { asPath } = useRouter();

  const [cashbackRef, setRef] = useState<RefObject<HTMLDivElement>>();
  const intl = useIntl();
  const user = useAuth(s => s.user, shallow);

  const hash = asPath.split('#')[1];
  const isMyWallyHashExist = hash === 'my-cashback';
  const [isCashbackExpanded, setIsCashBackExpanded] = useState(false);

  const { scrollToItem } = useScrollToItem();

  const isUserAuth = Boolean(user?.token);

  const { isFetchingBalance, isErrorBalance, isSuccessBalance, refetchBalance } =
    useCashbackBalance();
  const { userFormattedCashbackBalance } = useUserBalance();

  const {
    data: transactionsData,
    isFetching: isFetchingTransactions,
    isError: isErrorTransactions,
    refetch: refetchTransactions,
  } = useGetTransactions(
    {
      params: {
        limit: 10,
        page: 1,
        walletType: CASHBACK,
      },
    },
    {
      enabled: isUserAuth,
    }
  );

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setIsCashBackExpanded(isExpanded);
  };

  useEffect(() => {
    const ref = createRef<HTMLDivElement>();
    setRef(ref);
  }, []);

  useEffect(() => {
    if (isCashbackExpanded && cashbackRef && isMyWallyHashExist)
      cashbackRef.current && scrollToItem(cashbackRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashbackRef?.current, asPath]);

  useEffect(() => {
    if (isMyWallyHashExist) {
      setIsCashBackExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  return (
    <Zoom in timeout={PROFILE_CARDS_TRANSITION_TIMEOUT}>
      <Accordion
        sx={{ maxWidth: '910px' }}
        expanded={isCashbackExpanded}
        onChange={handleChange}
      >
        <AccordionSummary
          id={'open-cashback'}
          expandIcon={
            <ExpandMoreIcon
              sx={{
                color: theme.palette.supporting.navy,
                width: { tablet: pxToRem(24), mobile: '24px' },
                height: { tablet: pxToRem(24), mobile: '24px' },
                alignItems: 'center',
              }}
            />
          }
          sx={{
            ...accordionSx,
            justifyContent: 'space-between',
          }}
        >
          <Box
            component='div'
            sx={{
              ...accordionBoxIconSx,
              backgroundColor: theme.palette.supporting.navy,
            }}
          >
            <ImageBoxFuture
              img={CashbackIcon}
              title={intl.formatMessage({
                id: 'alt.cashback',
              })}
              objectFit='contain'
              sx={{
                width: { tablet: pxToRem(33), mobile: '40px' },
                height: { tablet: pxToRem(33), mobile: '24px' },
              }}
            />
          </Box>
          <Typography component='h2' sx={accordionTitleSx}>
            <FormattedMessage id='alt.cashback' />
          </Typography>
          <Typography
            id={'wallet-amount'}
            component='h2'
            sx={{
              ...accordionTitleSx,
              flexGrow: 1,
              textAlign: 'end',
              marginInlineEnd: '12px',
            }}
          >
            {isUserAuth ? (
              <>
                {userFormattedCashbackBalance}&nbsp;
                <FormattedMessage id='currency.SAR' />
              </>
            ) : (
              <FormattedMessage id='wallet.know_more' />
            )}
          </Typography>
        </AccordionSummary>
        {isUserAuth ? (
          isFetchingBalance ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <MovieLoaderWrapper outerBox={{ width: '200px' }} />
            </Box>
          ) : isErrorBalance ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                marginBlock: '64px',
              }}
            >
              <ImageBoxFuture
                img={NotfoundIcon}
                sx={{ width: pxToRem(288), height: pxToRem(178) }}
              />
              <Typography
                sx={{
                  fontSize: pxToRem(20),
                  fontWeight: '400',
                  color: theme.palette.grey[50],
                  marginTop: '34px',
                }}
              >
                <FormattedMessage id='movie_finder.sorry.something.went.wrong' />
              </Typography>
              <Button
                loading={isFetchingBalance}
                variant='contained'
                sx={{ marginTop: '32px', width: '178px', height: '60px' }}
                onClick={() => refetchBalance()}
              >
                <FormattedMessage id='global.try_again' />
              </Button>
            </Box>
          ) : isSuccessBalance ? (
            <CashbackAccordionDetails>
              <CashbackInfo
                myCashbackRef={cashbackRef}
                accordionSx={accordionSx}
                accordionBoxIconSx={accordionBoxIconSx}
                transactions={transactionsData?.data ?? []}
                isFetchingTransactions={isFetchingTransactions}
                isErrorTransactions={isErrorTransactions}
                refetchTransactions={refetchTransactions}
              />
            </CashbackAccordionDetails>
          ) : null
        ) : user?.isCompleteProfile && !user?.isVerified ? (
          <CashbackAccordionDetails>
            <WalletNotVerifiedInfo myWalletRef={cashbackRef} accordionSx={accordionSx} />
          </CashbackAccordionDetails>
        ) : !user?.isCompleteProfile && !user?.isVerified ? (
          <CashbackAccordionDetails>
            <CashbackNotCompletedProfileInfo
              myWalletRef={cashbackRef}
              accordionSx={accordionSx}
            />
          </CashbackAccordionDetails>
        ) : null}
      </Accordion>
    </Zoom>
  );
};

export default CashbackCollapse;
